// Small cell components shared by TableRows.js's DayRow/AvgRow across both
// Table modes (default and detailed).
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { band } from "../../../../../utils/nutrition-progress";
import { ADH_COLOR } from "./viewTokens";
import SvgIcon from "../../../../../parts/SvgIcon";
import {
  DUMBBELL_ICON,
  REST_ICON,
  DROP_ICON,
  CHEVRON_ICON,
  fmtDayDate,
  visibleTailKeys,
  TAIL_FIELD_CONFIG,
  dayShape,
} from "./TableFormat";

/* ---- shared cells ---------------------------------------------------------- */

// Whole cell is clickable — toggles the TableDetailPanel row rendered right
// below by DayRow (TableRows.js). Chevron points right when collapsed, down
// when expanded. The click/hover target is the
// <td> itself (not a nested button), and the <td> is NOT display:flex — that
// would break its table-cell participation and make it stop matching the
// row's height. Only the inner content is inline-flex, for icon+text layout.
// Only clickable when there's something to show in the detail panel — a day
// with no meal photos and no food log has nothing to expand into.
export const DateCell = ({ day, expanded, onToggle }) => {
  const hasDetail = day.photos > 0 || day.hasFoodLog;
  // onToggle takes utcDate (not pre-bound to this day) so TableView.js can
  // pass the same stable, useCallback-wrapped function to every row instead
  // of a fresh per-day closure — that per-render closure was the reason
  // DayRow/AvgRow couldn't be memoized (see TableRows.js's own comment).
  const handleToggle = () => onToggle(day.utcDate);
  return (
    <td
      className={`np-table-cell np-table-cell--left${hasDetail ? " np-table-date-toggle" : ""}`}
      tabIndex={hasDetail ? 0 : undefined}
      onClick={hasDetail ? handleToggle : undefined}
      onKeyDown={
        hasDetail
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggle();
              }
            }
          : undefined
      }
    >
      <span className="np-table-date-toggle__inner">
        <span className="np-table-date-chevron-slot">
          {hasDetail && (
            <SvgIcon
              src={CHEVRON_ICON}
              useCurrentColor
              classNames={`np-table-date-chevron${expanded ? " np-table-date-chevron--open" : ""}`}
            />
          )}
        </span>
        <span className="np-table-date-dot" style={{ background: ADH_COLOR[band(day)].text }} />
        {fmtDayDate(day.utcDate)}
      </span>
    </td>
  );
};

// Regular-weight "–" (no data) or real value cell — the wearable/vitals tail
// columns (Wt/Steps/Sleep/HRV/RHR/RPE/Water/Body fat) all share this pattern:
// muted gray only for the no-data placeholder, default color for a real value.
const TailStat = ({ value, children }) => (
  <td
    className="np-table-cell np-table-cell--edge np-table-cell--regular"
    style={value == null ? { color: ADH_COLOR.none.text } : undefined}
  >
    {children}
  </td>
);

// The "Day" tail cell is the one key with no single value/formatter (see
// TAIL_FIELD_CONFIG's comment) — a workout/rest icon + optional period drop,
// so it stays its own small component instead of a config entry.
const DayTypeCell = ({ day }) => (
  <td className="np-table-cell np-table-cell--edge">
    {day.workout ? (
      <SvgIcon
        src={DUMBBELL_ICON}
        useCurrentColor
        classNames="np-table-day-icon"
        style={{ color: "var(--gradient-right)" }}
      />
    ) : (
      <SvgIcon
        src={REST_ICON}
        useCurrentColor
        classNames="np-table-day-icon"
        style={{ color: ADH_COLOR.none.text }}
      />
    )}
    {day.period && (
      <SvgIcon
        src={DROP_ICON}
        useCurrentColor
        classNames="np-table-day-icon ml-1"
        style={{ color: "var(--sore)" }}
      />
    )}
  </td>
);

// Trailing wt / steps / sleep / hrv / rhr / readiness / rpe / water /
// body-fat / day-type cells, shared by every day row — filtered to whichever
// keys the active template (if any) leaves visible. Every key but "days"
// reads TAIL_FIELD_CONFIG (TableFormat.js) generically: same field name on
// `day` as on an avgOf() result, so AvgTail below reuses the same config.
export const DayTail = ({ day, displayWt, displayWater, displayRpe, activeColumns }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const ctx = { displayWt, displayWater, displayRpe, t };
  return visibleTailKeys(activeColumns).map((key) => {
    if (key === "days") return <DayTypeCell key={key} day={day} />;
    const { field, format } = TAIL_FIELD_CONFIG[key];
    const value = day[field];
    return (
      <TailStat key={key} value={value}>
        {format(value, ctx)}
      </TailStat>
    );
  });
};

// Gradient text (same as the thead group labels) for Average / Overall
// Average row values — wraps an inline-block sized to the text itself, not
// the <td>, so background-clip:text samples the full gradient (see
// .np-table-gradient-text).
export const GradientText = ({ children }) => <span className="np-table-gradient-text">{children}</span>;

// Gradient text for a real average value, muted gray "–" when there's nothing
// to average (e.g. no day in the group has a plan target for that macro) —
// mirrors DayTail's convention: gray is reserved for the no-data placeholder,
// never applied to a real value.
export const AvgText = ({ value, children }) =>
  value == null ? (
    <span className="np-table-cell--muted">{children}</span>
  ) : (
    <GradientText>{children}</GradientText>
  );

// Trailing wt / steps / sleep / hrv / rhr / readiness / rpe / water /
// body-fat / logged-count cells, shared by every average row — same
// TAIL_FIELD_CONFIG-driven pattern as DayTail (identical field names on
// `avg` as on a day row), "days" special-cased as the n/total count instead
// of the workout/rest icon.
export const AvgTail = ({ avg, displayWt, displayWater, displayRpe, activeColumns }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const ctx = { displayWt, displayWater, displayRpe, t };
  return visibleTailKeys(activeColumns).map((key) => {
    if (key === "days") {
      return (
        <td key={key} className="np-table-cell np-table-cell--edge np-table-cell--muted">
          {avg.n}/{avg.total}
        </td>
      );
    }
    const { field, format } = TAIL_FIELD_CONFIG[key];
    const value = avg[field];
    return (
      <td key={key} className="np-table-cell np-table-cell--edge">
        <AvgText value={value}>{format(value, ctx)}</AvgText>
      </td>
    );
  });
};

export const LabelCell = ({ label }) => (
  <td className="np-table-cell np-table-cell--left">
    <GradientText>{label}</GradientText>
  </td>
);

// One merged cell PER column-group (Consumed + Adherence for default,
// Target/Consumed/Adherence for detailed) — not one giant merge — so every
// group boundary still shows its divider with no data. Wt/Steps/Sleep/Day
// render as real cells via AvgTail for the same reason.
// groupSpans can be empty (a template hiding every macro/adherence group) —
// there's then no cell left to carry the "no logged days" message, so it
// moves into the label itself instead of silently disappearing.
export const EmptyAvgRow = ({ label, avg, displayWt, displayWater, displayRpe, activeColumns, groupSpans }) => {
  const { t } = useTranslation("athlete-portal");
  const noLoggedDays = t("nutritionPage.progress.table.noLoggedDays");
  return (
    <tr className="np-table-avg-row">
      <LabelCell label={groupSpans.length ? label : `${label} — ${noLoggedDays}`} />
      {groupSpans.map(({ key, span }, i) => (
        <td
          key={key}
          colSpan={span}
          className="np-table-cell np-table-cell--edge np-table-cell--muted"
        >
          {i === 0 ? noLoggedDays : "–"}
        </td>
      ))}
      <AvgTail
        avg={avg}
        displayWt={displayWt}
        displayWater={displayWater}
        displayRpe={displayRpe}
        activeColumns={activeColumns}
      />
    </tr>
  );
};

export const activeColumnsPropType = PropTypes.instanceOf(Set);

TailStat.propTypes = { value: PropTypes.number, children: PropTypes.node };
DayTypeCell.propTypes = { day: dayShape };
DateCell.propTypes = { day: dayShape, expanded: PropTypes.bool, onToggle: PropTypes.func };
DayTail.propTypes = {
  day: dayShape,
  displayWt: PropTypes.func,
  displayWater: PropTypes.func,
  displayRpe: PropTypes.func,
  activeColumns: activeColumnsPropType,
};
GradientText.propTypes = { children: PropTypes.node };
AvgText.propTypes = { value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), children: PropTypes.node };
AvgTail.propTypes = {
  avg: PropTypes.object,
  displayWt: PropTypes.func,
  displayWater: PropTypes.func,
  displayRpe: PropTypes.func,
  activeColumns: activeColumnsPropType,
};
LabelCell.propTypes = { label: PropTypes.string };
EmptyAvgRow.propTypes = {
  label: PropTypes.string,
  avg: PropTypes.object,
  displayWt: PropTypes.func,
  displayWater: PropTypes.func,
  displayRpe: PropTypes.func,
  activeColumns: activeColumnsPropType,
  groupSpans: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, span: PropTypes.number })
  ),
};
